import { deserialize, Expose, Serializable, Transform } from '@tokeet-frontend/tv3-platform'
import { PlanActivity } from '@tv3/types/plan-activity'
import { toMoment } from '@tokeet-frontend/tv3-platform'
import { SubscriptionDiscount } from '@tokeet-frontend/plans'

export enum ProductsForPlan {
  AdvanceCM = 'tokeet',
  Automata = 'automata',
  Signature = 'signature',
  Checklist = 'checklist',
  OwnerCenter = 'owner_center',
  Aireply = 'aireply',
  Rategenie = 'rategenie',
  Staffmagic = 'staffmagic',
  Webready = 'webready',
  PowerReporting = 'power_reporting',
  TokeetAI = 'tokeet_ai',
  SmartDevice = 'tokeet_smart_devices',
  Messaging = 'tokeet_messaging',
  CustomPhone = 'tokeet_messaging_custom_phone',
}

export const tokeetAddonProductNames = {
  [ProductsForPlan.AdvanceCM]: 'AdvanceCM',
  [ProductsForPlan.Automata]: 'Automata',
  [ProductsForPlan.Signature]: 'Signature',
  [ProductsForPlan.OwnerCenter]: 'Owner Center',
  [ProductsForPlan.Aireply]: 'Aireply',
  [ProductsForPlan.Rategenie]: 'Rategenie',
  [ProductsForPlan.Staffmagic]: 'Staffmagic',
  [ProductsForPlan.Webready]: 'Websites & Widgets',
  [ProductsForPlan.PowerReporting]: 'Power Reporting',
  [ProductsForPlan.SmartDevice]: 'Smart Device',
  [ProductsForPlan.Messaging]: 'Messaging',
  [ProductsForPlan.CustomPhone]: 'Custom Phone',
}

export enum AdvanceCMAdditionalFeatureIds {
  Payments = 'tokeet_payments',
  ChannelManager = 'tokeet_channel_manager',
  PropertyManager = 'tokeet_property_manager',
}
export const advanceCMAdditionalFeatures: {
  [key: string]: { shortName: string; name: string; description: string; price: string }
} = {
  [AdvanceCMAdditionalFeatureIds.ChannelManager]: {
    shortName: $localize`Channel Manager`,
    name: $localize`AdvanceCM API Channel Manager`,
    description: $localize`Synchronize your rates, bookings, and availability in real-time.`,
    price: '',
  },
  [AdvanceCMAdditionalFeatureIds.Payments]: {
    shortName: $localize`Payments`,
    name: $localize`AdvanceCM Payments`,
    description: $localize`Accept bookings online and send invoices to guests.`,
    price: '0.5%',
  },
  [AdvanceCMAdditionalFeatureIds.PropertyManager]: {
    shortName: $localize`Property Manager`,
    name: $localize`AdvanceCM Property Management System`,
    description: $localize`Manage your bookings, guests, and rentals all in one place.`,
    price: 'FREE',
  },
}

export enum PlanIntervalTypes {
  Monthly = 'month',
  Yearly = 'year',
}

export enum PlanTypes {
  Free = 'free',
  Entry = 'entry',
  Owner = 'owner',
  Agent = 'agent',
  Manager = 'manager',
  Unlimited = 'unlimited',
  TokeetV4 = 'tokeet',
  TokeetV5 = 'tokeet',
}

export const planTypeLevels = {
  [PlanTypes.Free]: -1,
  [PlanTypes.Entry]: 0,
  [PlanTypes.Owner]: 1,
  [PlanTypes.Agent]: 2,
  [PlanTypes.Manager]: 3,
  [PlanTypes.Unlimited]: 4,
  // TokeetV5 has the same value ('tokeet') as TokeetV4, so this entry covers both.
  [PlanTypes.TokeetV4]: 5,
}

export const allPlansFeatures = {
  [PlanTypes.Free]: [$localize`Reservation Manager`],
  [PlanTypes.Entry]: [$localize`Reservation Manager`, $localize`Channel Manager`, $localize`Mobile App`],
  [PlanTypes.Owner]: [
    $localize`Reservation Manager`,
    $localize`Channel Manager`,
    $localize`Mobile App`,
    $localize`Website Builder`,
    $localize`Payment Gateway`,
    $localize`Integrations`,
  ],
  [PlanTypes.Agent]: [
    $localize`Reservation Manager`,
    $localize`Channel Manager`,
    $localize`Mobile App`,
    $localize`Website Builder`,
    $localize`Payment Gateway`,
    $localize`Integrations`,
    $localize`Custom Branding`,
  ],
  [PlanTypes.Manager]: [
    $localize`Reservation Manager`,
    $localize`Channel Manager`,
    $localize`Mobile App`,
    $localize`Website Builder`,
    $localize`Payment Gateway`,
    $localize`Integrations`,
    $localize`Custom Branding`,
  ],
  [PlanTypes.Unlimited]: [
    $localize`Reservation Manager`,
    $localize`Channel Manager`,
    $localize`Mobile App`,
    $localize`Website Builder`,
    $localize`Payment Gateway`,
    $localize`Integrations`,
    $localize`Custom Branding`,
  ],
  // TokeetV5 has the same value ('tokeet') as TokeetV4, so this entry covers both.
  [PlanTypes.TokeetV4]: [
    $localize`Reservation Manager`,
    $localize`Channel Manager`,
    $localize`Mobile App`,
    $localize`Website Builder`,
    $localize`Payment Gateway`,
    $localize`Integrations`,
    $localize`Custom Branding`,
  ],
}

export const unlimitedUsersThreshold = 200
export const unlimitedRentalsThreshold = 200

export const currentFreeTokeetPlanId = 'free_v3'

export class ActivePlanStatus extends Serializable<ActivePlanStatus> {
  @Expose({ name: 'plan_id' })
  planId: string

  @Expose({ name: 'plan_name' })
  @Transform(transformPlanName)
  planName: string

  rentals: number
  users: number
  product: string
  status: PlanActivity
  price: number

  trial_start: number
  trial_end: number
  start: number
  until: number
  timestamp: number
  products: string[]
  delinquent: boolean

  static deserialize(data: object): ActivePlanStatus {
    return deserialize(ActivePlanStatus, data)
  }
}

export class PlanOption extends Serializable<PlanOption> {
  id: string
  amount: number
  rentals: number
  name: string
  interval: 'month' | 'year'
  trial: string
  product: string
  interval_count: number
  users: number
  value: string

  static deserialize(data: object): PlanOption {
    return deserialize(PlanOption, data)
  }
}

export interface SubscriptionDto {
  plan_id: string
  trial_start: number
  trial_end: number
  cancel_at_period_end: boolean
  plan_name: string
  product: string
  gateway: string
  start: number
  plan: string
  users: number
  until: number
  rentals: number
  status: string
  txn: string
  timestamp: number
  price: number
}

export class PlanSubscription extends Serializable<PlanSubscription> {
  @Expose({ name: 'plan_id' })
  planId: string

  @Expose({ name: 'trial_start' })
  trialStart: number

  @Expose({ name: 'trial_end' })
  trialEnd: number

  @Expose({ name: 'cancel_at_period_end' })
  cancelAtPeriodEnd: boolean

  @Expose({ name: 'plan_name' })
  @Transform(transformPlanName)
  planName: string

  product: string
  gateway: string
  start: number
  plan: string
  users: number
  until: number
  rentals: number
  quantity?: number
  status: string
  txn: string
  timestamp: number
  created: number
  price: number
  discount?: SubscriptionDiscount

  static deserialize(data: object): PlanSubscription {
    return deserialize(PlanSubscription, data)
  }
}

export interface ActivePlansByProduct {
  [product: string]: PlanSubscription
}

export function isSubscriptionValid(sub: PlanSubscription) {
  const now = toMoment().unix()

  if (!sub) return false

  if (sub.trialEnd && sub.trialEnd > now) {
    return true
  }

  return sub.until >= now
}

// TV4-6387: true for a past_due subscription whose first invoice after its
// signup trial failed. Stripe starts the first paid period at trial_end, and a
// signup trial starts when the subscription is created. Both checks allow one
// hour for small gaps between the Stripe timestamps. A record without these
// fields counts as paid.
export function isFailedTrialConversion(sub: PlanSubscription) {
  if (sub?.status !== 'past_due') return false
  if (!sub.start || !sub.trialStart || !sub.trialEnd || !sub.created) return false

  return sub.start <= sub.trialEnd + 3600 && sub.trialStart <= sub.created + 3600
}

function transformPlanName(value: string) {
  value = String(value || '')

  return value.replace('Tokeet Messaging', 'Advance Messaging')
}
